import type { ICreateSubscriptionCmd } from "@/data/commands/subscription/CreateSubscriptionCmd";
import { Factory } from "@/data/dao/Factory";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { SubscriptionDao } from "@/data/dao/service/SubscriptionDao";
import type { ILocation } from "@/data/model/Location";
import type { ISubscription, ISubscriptionPreset } from "@/data/model/Subscription";

import { DatePicker } from "@/portal/formControls/DatePicker";
import { NumberInput } from "@/portal/formControls/NumberInput";
import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";

export class SubscriptionForm extends BaseEntityForm<ISubscription, ICreateSubscriptionCmd> {
    private locationSelect: Select<ILocation>;
    private presetSelect: Select<ISubscriptionPreset>;

    private validUntilInput: DatePicker;

    private canUseCovidToggle: Switch;
    private canVerifyCertToggle: Switch;
    private canShowGuestNumberToggle: Switch;
    private canContinueTrackingToggle: Switch;
    private canUseOrderToggle: Switch;
    private canUseDocumentsToggle: Switch;
    private canUseUrlToggle: Switch;
    private canUseOfferToggle: Switch;
    private canUsePayToggle: Switch;
    private canUseRoomPlanToggle: Switch;

    private $descriptionField: JQuery;
    private maxDailyVisitoryInput: NumberInput;
    private maxDocumentsInput: NumberInput;
    private maxLocationInput: NumberInput;
    private maxRoomInput: NumberInput;
    private maxTableInput: NumberInput;
    private presetsLoaded = false;

    constructor(element: JQuery) {
        super(element, Factory.getInstance(SubscriptionDao));

        this.locationSelect = Select.createFromSelect(
            this.$form.find('.form-control[name="location"]'),
            Factory.getInstance(LocationDao),
            (c) => c.name
        );
        this.presetSelect = Select.createFromSelect(
            this.$form.find('.form-control[name="preset"]'),
            Factory.getInstance<SubscriptionDao>(SubscriptionDao).presetDataSource,
            (c) => c.name,
            true,
            "Kundenspezifisch"
        );

        this.presetSelect.onChange = () => {
            this.setByPreset(this.presetSelect.getModel());
        };

        this.validUntilInput = new DatePicker(this.$form.find('.form-control[name="validuntilinput"]'));

        this.canUseCovidToggle = new Switch(this.$form.find('input[name="canusecovidtoggle"]'));
        this.canUseCovidToggle.onChange = () => this.checkCovidSubsettingsVisibility();

        this.canVerifyCertToggle = new Switch(this.$form.find('input[name="canverifycerttoggle"]'));
        this.canShowGuestNumberToggle = new Switch(this.$form.find('input[name="canshowguestnumbertoggle"]'));
        this.canContinueTrackingToggle = new Switch(this.$form.find('input[name="cancontinuetrackingtoggle"]'));
        this.canUseOrderToggle = new Switch(this.$form.find('input[name="canuseordertoggle"]'));
        this.canUseDocumentsToggle = new Switch(this.$form.find('input[name="canusedocumentstoggle"]'));
        this.canUseDocumentsToggle.onChange = () => this.checkDocumenSubsettingsVisibility();

        this.canUseUrlToggle = new Switch(this.$form.find('input[name="canuseurltoggle"]'));
        this.canUseOfferToggle = new Switch(this.$form.find('input[name="canuseoffertoggle"]'));
        this.canUsePayToggle = new Switch(this.$form.find('input[name="canusepaytoggle"]'));
        this.canUseRoomPlanToggle = new Switch(this.$form.find('input[name="canuseroomplantoggle"]'));

        this.maxDailyVisitoryInput = new NumberInput(this.$form.find('.form-control[name="maxdailyvisitorsinput"]'));
        this.maxDocumentsInput = new NumberInput(this.$form.find('.form-control[name="maxdocumentsinput"]'));
        this.maxLocationInput = new NumberInput(this.$form.find('.form-control[name="maxlocationsinput"]'));
        this.maxTableInput = new NumberInput(this.$form.find('.form-control[name="maxtablesinput"]'));
        this.maxRoomInput = new NumberInput(this.$form.find('.form-control[name="maxroomsinput"]'));

        this.$descriptionField = this.$form.find('.form-control[name="description"]');
    }

    public setFormData(model?: ISubscription) {
        this.$descriptionField.val(model === undefined || model.description === undefined ? "" : model.description);
        this.validUntilInput.setValue(model === undefined ? undefined : model.validUntil);

        this.canUseCovidToggle.setValue(model === undefined ? false : model.canUseCovid);
        this.checkCovidSubsettingsVisibility();
        this.canVerifyCertToggle.setValue(model === undefined ? false : model.canVerifyCert);
        this.canShowGuestNumberToggle.setValue(model === undefined ? false : model.canShowGuestNumber);
        this.maxDailyVisitoryInput.setValue(model === undefined ? undefined : model.maxDailyVisitors);
        this.canContinueTrackingToggle.setValue(model === undefined ? false : model.canContinueTracking);
        this.canUseOrderToggle.setValue(model === undefined ? false : model.canUseOrder);
        this.canUseOfferToggle.setValue(model === undefined ? false : model.canUseOffer);
        this.canUsePayToggle.setValue(model === undefined ? false : model.canUsePay);
        this.canUseRoomPlanToggle.setValue(model === undefined ? false : model.canUseRoomPlan);

        this.canUseDocumentsToggle.setValue(model === undefined ? false : model.canUseDocuments);
        this.checkDocumenSubsettingsVisibility();
        this.maxDocumentsInput.setValue(model === undefined ? undefined : model.maxDocuments);

        this.canUseUrlToggle.setValue(model === undefined ? false : model.canUseUrl);

        if (model !== undefined) this.locationSelect.setValue(model.locationId).setDisabled(true);

        this.maxLocationInput.setValue(model === undefined ? undefined : model.maxLocations);
        this.maxRoomInput.setValue(model === undefined ? undefined : model.maxRooms);
        this.maxTableInput.setValue(model === undefined ? undefined : model.maxTables);

        const preset = model === undefined ? undefined : model.presetId;
        this.presetSelect.setValue(preset);

        if (!this.presetsLoaded) {
            this.presetsLoaded = true;
            this.presetSelect.fetchData(() => {
                this.presetSelect.setValue(preset);
                this.setByPreset(this.presetSelect.getModel());
            });
        }
    }

    private setByPreset(p?: ISubscriptionPreset) {
        if (p === undefined) {
            this.canUseCovidToggle.setDisabled(false);
            this.maxDailyVisitoryInput.setDisabled(false);
            this.canVerifyCertToggle.setDisabled(false);
            this.canShowGuestNumberToggle.setDisabled(false);
            this.canContinueTrackingToggle.setDisabled(false);
            this.canUseOrderToggle.setDisabled(false);
            this.canUseDocumentsToggle.setDisabled(false);
            this.maxDocumentsInput.setDisabled(false);
            this.canUseUrlToggle.setDisabled(false);
            this.canUseOfferToggle.setDisabled(false);
            this.canUsePayToggle.setDisabled(false);
            this.canUseRoomPlanToggle.setDisabled(false);
            this.maxLocationInput.setDisabled(false);
            this.maxRoomInput.setDisabled(false);
            this.maxTableInput.setDisabled(false);
        } else {
            this.canUseCovidToggle.setDisabled(false).setValue(p.canUseCovid).setDisabled(true);
            this.checkCovidSubsettingsVisibility();
            this.maxDailyVisitoryInput.setDisabled(false).setValue(p.maxDailyVisitors).setDisabled(true);
            this.canVerifyCertToggle.setDisabled(false).setValue(p.canVerifyCert).setDisabled(true);
            this.canShowGuestNumberToggle.setDisabled(false).setValue(p.canShowGuestNumber).setDisabled(true);
            this.canContinueTrackingToggle.setDisabled(false).setValue(p.canContinueTracking).setDisabled(true);
            this.canUseOrderToggle.setDisabled(false).setValue(p.canUseOrder).setDisabled(true);
            this.canUseDocumentsToggle.setDisabled(false).setValue(p.canUseDocuments).setDisabled(true);
            this.maxDocumentsInput.setDisabled(false).setValue(p.maxDocuments).setDisabled(true);
            this.canUseUrlToggle.setDisabled(false).setValue(p.canUseUrl).setDisabled(true);

            this.maxLocationInput.setDisabled(false).setValue(p.maxLocations).setDisabled(true);
            this.maxRoomInput.setDisabled(false).setValue(p.maxRooms).setDisabled(true);
            this.maxTableInput.setDisabled(false).setValue(p.maxTables).setDisabled(true);
            this.canUseOfferToggle.setDisabled(false).setValue(p.canUseOffer).setDisabled(true);
            this.canUsePayToggle.setValue(p.canUsePay).setDisabled(true);
            this.canUseRoomPlanToggle.setValue(p.canUseRoomPlan).setDisabled(true);
        }
    }

    private checkCovidSubsettingsVisibility() {
        if (this.canUseCovidToggle.getValue()) {
            this.$form.find(".covidsection .form-group:not(.canusecovidenable)").show();
            this.$form.find(".covidsection .form-group.canusecovidenable").removeClass("form-group-last");
        } else {
            this.$form.find(".covidsection .form-group:not(.canusecovidenable)").hide();
            this.$form.find(".covidsection .form-group.canusecovidenable").addClass("form-group-last");
        }
    }

    private checkDocumenSubsettingsVisibility() {
        if (this.canUseDocumentsToggle.getValue()) {
            this.$form.find(".documentsection .form-group:not(.canusedocumentenable)").show();
            this.$form.find(".documentsection .form-group.canusedocumentenable").removeClass("form-group-last");
        } else {
            this.$form.find(".documentsection .form-group:not(.canusedocumentenable)").hide();
            this.$form.find(".documentsection .form-group.canusedocumentenable").addClass("form-group-last");
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateSubscriptionCmd {
        const data: ICreateSubscriptionCmd = {
            locationId: this.locationSelect.getValue()!,
            validUntil: this.validUntilInput.getValue(),
            description: this.$descriptionField.val().length === 0 ? undefined : this.$descriptionField.val(),
        };

        const preset = this.presetSelect.getValue();

        if (preset === undefined) {
            const useCovid = this.canUseCovidToggle.getValue();

            data.canUseCovid = useCovid;
            data.maxDailyVisitors = useCovid ? this.maxDailyVisitoryInput.getValue() : undefined;
            data.canContinueTracking = useCovid ? this.canContinueTrackingToggle.getValue() : undefined;
            data.canShowGuestNumber = useCovid ? this.canShowGuestNumberToggle.getValue() : undefined;
            data.canVerifyCert = useCovid ? this.canVerifyCertToggle.getValue() : undefined;

            const useDocuments = this.canUseDocumentsToggle.getValue();

            data.canUseDocuments = useDocuments;
            data.maxDocuments = useDocuments ? this.maxDocumentsInput.getValue() : undefined;

            data.canUseOrder = this.canUseOrderToggle.getValue();
            data.canUseOffer = this.canUseOfferToggle.getValue();
            data.canUsePay = this.canUsePayToggle.getValue();
            data.canUseRoomPlan = this.canUseRoomPlanToggle.getValue();
            data.canUseUrl = this.canUseUrlToggle.getValue();
            data.maxLocations = this.maxLocationInput.getValue();
            data.maxRooms = this.maxRoomInput.getValue();
            data.maxTables = this.maxTableInput.getValue();
        } else {
            data.presetId = preset;
        }

        const hasError = false;
        return (hasError ? undefined : data) as any;
    }
}
